import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { verifyEmail, me } from "../../api/auth.api";
import useAuthStore from "../../store/auth.store";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ status: "loading", message: "" });
  const calledRef = useRef(false);
  const setAuth = useAuthStore((s) => s.setAuth);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    if (!token) {
      setState({ status: "error", message: "No verification token in the URL." });
      return;
    }
    (async () => {
      try {
        await verifyEmail(token);
        setState({ status: "ok", message: "Email verified. Welcome aboard!" });
        // Post-verify routing: superusers → /admin, everyone else →
        // api-tokens with the welcome banner. Refresh /me first so the
        // is_superuser flag is current (verifyEmail may have signed
        // the recipient in if they weren't already).
        let target = "/cricket/api-tokens?welcome=1";
        try {
          const { data } = await me();
          if (data) setAuth(data);
          if (data?.is_superuser) target = "/admin";
        } catch { /* keep default target */ }
        setTimeout(() => {
          navigate(target, { replace: true });
        }, 1500);
      } catch (e) {
        const detail = e?.response?.data?.detail || "Verification link is invalid or has expired.";
        setState({ status: "error", message: detail });
      }
    })();
  }, [token, navigate, setAuth]);

  return (
    <div className="min-h-screen bg-bg-main text-text-primary flex flex-col items-center justify-center px-6 py-10">
      <Link to="/" className="font-bold text-xl tracking-tight mb-8">
        Zen<span className="text-button-primary">cricket</span>data
      </Link>

      <div className="w-full max-w-md bg-bg-primary border border-border-default rounded-2xl p-8 sm:p-10 text-center">
        {state.status === "loading" && (
          <>
            <Loader2 className="w-10 h-10 text-button-primary animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold">Verifying your email…</h1>
          </>
        )}

        {state.status === "ok" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-2xl font-extrabold mb-2">{state.message}</h1>
            <p className="text-text-secondary text-sm">Redirecting you to your API tokens…</p>
          </>
        )}

        {state.status === "error" && (
          <>
            <XCircle className="w-12 h-12 text-button-primary mx-auto mb-4" />
            <h1 className="text-2xl font-extrabold mb-2">Verification failed</h1>
            <p className="text-text-secondary text-sm mb-6">{state.message}</p>
            <Link
              to="/login"
              className="inline-block bg-button-primary hover:brightness-110 text-white font-semibold py-2.5 px-5 rounded-lg transition-all"
            >
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
